import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Box, Button, CircularProgress, IconButton } from "@mui/material"
import Parse from "parse"
import moment from "moment"
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu"
import MenuIcon from "@mui/icons-material/Menu"

import GenericTopBarHeader from "../../../../components/GenericTopBarHeader"
import PageHeaderTitle from "../../../../components/PageHeaderTitle"
import { COLORS, getProdDayFilterWindow } from "../../../../utils"
import { showDeliveryMenus } from "../../../../actions/Organizations/DeliveryMenus"
import RecipeStepper from "./RecipeStepper"
import RecipeCalendar from "./RecipeCalendar"
import RecipeCatalogPanel from "./RecipeCatalogPanel"
import { openAppMessageSnackBar } from "../../../../actions/Utils/app"

const DUPLICATE_MESSAGE = "Ce plat est déjà placé sur cette commande pour ce jour de prod"
const ERROR_MESSAGE = "Une erreur est survenue, veuillez réessayer."
const EMPTY_SLOT_MESSAGE = "Chaque commande doit contenir au moins une recette"

const PANEL_WIDTH = 385
const PANEL_COLLAPSED_WIDTH = 44

const fetchDeliveriesByIds = (deliveryIds) =>
  Parse.Cloud.run("getDeliveriesByIds", { deliveryIds })

const saveDeliveryCardContents = (deliveryId, placements) =>
  Parse.Cloud.run("saveDeliveryCardContents", {
    deliveryId,
    cardContents: placements.map((p) => ({ recipeId: p.recipe.recipeId, productionDate: p.prodDate ?? null })),
  })

const buildDeliveryDaysFromDeliveries = (deliveries = [], enterpriseCode) => {
  const byDate = {}
  deliveries.forEach((d) => {
    const date = moment(d.deliveryDate).format("YYYY-MM-DD")
    if (!byDate[date]) byDate[date] = []
    const siteLabel = enterpriseCode ? `${enterpriseCode} - ${d.dcCode || d.dcName || "Site"}` : (d.dcName || "Site")
    byDate[date].push({ id: d.objectId, dcName: siteLabel })
  })
  return Object.keys(byDate).sort().map((date) => ({
    date,
    label: moment(date).format("dddd D MMMM"),
    slots: byDate[date],
  }))
}

const buildRecipesBySlotFromDeliveries = (deliveries = [], recipes = []) => {
  const recipeById = Object.fromEntries(recipes.map((r) => [r.recipeId, r]))
  const bySlot = {}
  deliveries.forEach((d) => {
    const placements = (d.cardContents || [])
      .map((entry) => {
        const recipe = recipeById[entry.recipeId]
        return recipe ? { recipe, prodDate: entry.productionDate ?? null } : null
      })
      .filter(Boolean)
    if (placements.length > 0) bySlot[d.objectId] = placements
  })
  return bySlot
}

const normalizeServerRecipe = (r) => ({
  recipeId: r.recipeId,
  code: r.uniqueCode,
  name: r.name,
  cat: r.productType,
  catLabel: r.productTypeLabel || r.productType,
  composition: undefined,
  categoryTags: r.categoryTags || [],
  dishTypeTags: r.dishTypeTags || [],
  foodcost: r.foodcost,
  prodDates: (r.prodDates || []).map((p) => p.date),
  dlcByDate: Object.fromEntries((r.prodDates || []).map((p) => [p.date, p.dlc])),
})

const fetchRecipeCatalog = async ({ enterpriseId, startDate, endDate }) => {
  const results = await Parse.Cloud.run("getRecipeCatalogForDelivery", { enterpriseId, startDate, endDate })
  return results.map(normalizeServerRecipe)
}

const DeliveryMenuRecipe = ({ location }) => {
  const dispatch = useDispatch()
  const deliveryIds = location.query.deliveryIds ? location.query.deliveryIds.split(",") : []
  const hasParams = deliveryIds.length > 0

  const [enterpriseName, setEnterpriseName] = useState("")
  const [deliveryDays, setDeliveryDays] = useState([])
  const [pageLoading, setPageLoading] = useState(hasParams)
  const [recipes, setRecipes] = useState([])
  const [searchValue, setSearchValue] = useState("")
  const [activeCat, setActiveCat] = useState("all")
  const [selectedProdDays, setSelectedProdDays] = useState([])
  const [selectedCategoryTagIds, setSelectedCategoryTagIds] = useState([])
  const [selectedDishTypeTagIds, setSelectedDishTypeTagIds] = useState([])
  const [panelOpen, setPanelOpen] = useState(true)
  const [recipesBySlot, setRecipesBySlot] = useState({})
  const [draggedRecipeId, setDraggedRecipeId] = useState(null)
  const [dropTarget, setDropTarget] = useState(null)
  const dragInfoRef = useRef(null)

  const headerSubtitle = hasParams ? enterpriseName : "AXA"

  const categoryOrder = useMemo(() => {
    const counts = new Map()
    recipes.forEach((r) => {
      if (!r.cat) return
      const entry = counts.get(r.cat) || { id: r.cat, label: r.catLabel || r.cat, count: 0 }
      entry.count += 1
      counts.set(r.cat, entry)
    })
    return [...counts.values()].sort((a, b) => b.count - a.count)
  }, [recipes])

  const catRank = useMemo(() => new Map(categoryOrder.map((c, i) => [c.id, i])), [categoryOrder])

  const handleBack = () => {
    const allSlotIds = deliveryDays.flatMap((day) => day.slots.map((slot) => slot.id))
    const hasEmptySlot = allSlotIds.some((slotId) => (recipesBySlot[slotId] || []).length === 0)
    if (hasEmptySlot) {
      dispatch(openAppMessageSnackBar(EMPTY_SLOT_MESSAGE, "error"))
      return
    }
    dispatch(showDeliveryMenus())
  }

  useEffect(() => {
    if (!hasParams) return
    let cancelled = false

    fetchDeliveriesByIds(deliveryIds)
      .then((result) => {
        if (cancelled) return
        setEnterpriseName(result.enterpriseName)
        const days = buildDeliveryDaysFromDeliveries(result.deliveries, result.enterpriseCode)
        setDeliveryDays(days)

        const { start, end } = getProdDayFilterWindow(days)
        return fetchRecipeCatalog({ enterpriseId: result.enterpriseId, startDate: start.valueOf(), endDate: end.valueOf() })
          .then((catalog) => {
            if (cancelled) return
            setRecipes(catalog)
            setRecipesBySlot(buildRecipesBySlotFromDeliveries(result.deliveries, catalog))
          })
          .catch(() => { if (!cancelled) dispatch(openAppMessageSnackBar(ERROR_MESSAGE, "error")) })
          .finally(() => { if (!cancelled) setPageLoading(false) })
      })
      .catch(() => {
        if (cancelled) return
        setPageLoading(false)
        dispatch(openAppMessageSnackBar(ERROR_MESSAGE, "error"))
      })

    return () => { cancelled = true }
  }, [])

  const persistSlot = (slotId, placements) => {
    saveDeliveryCardContents(slotId, placements).catch(() => {
      dispatch(openAppMessageSnackBar(ERROR_MESSAGE, "error"))
    })
  }

  const addPlacementToSlot = (slotId, placement, insertBeforeRecipeId) => {
    const current = recipesBySlot[slotId] || []
    if (current.some((p) => p.recipe.recipeId === placement.recipe.recipeId)) {
      dispatch(openAppMessageSnackBar(DUPLICATE_MESSAGE, "error"))
      return
    }
    const index = insertBeforeRecipeId ? current.findIndex((p) => p.recipe.recipeId === insertBeforeRecipeId) : -1
    const next = [...current]
    next.splice(index === -1 ? next.length : index, 0, placement)
    setRecipesBySlot({ ...recipesBySlot, [slotId]: next })
    persistSlot(slotId, next)
  }

  const handleRemove = (slotId, recipeId) => {
    const next = (recipesBySlot[slotId] || []).filter((p) => p.recipe.recipeId !== recipeId)
    setRecipesBySlot((prev) => ({ ...prev, [slotId]: next }))
    persistSlot(slotId, next)
  }

  const handleRemoveEverywhere = (recipeId) => {
    const affectedSlotIds = Object.keys(recipesBySlot).filter((slotId) =>
      (recipesBySlot[slotId] || []).some((p) => p.recipe.recipeId === recipeId)
    )
    if (affectedSlotIds.length === 0) return

    const next = { ...recipesBySlot }
    affectedSlotIds.forEach((slotId) => {
      next[slotId] = recipesBySlot[slotId].filter((p) => p.recipe.recipeId !== recipeId)
    })
    setRecipesBySlot(next)
    affectedSlotIds.forEach((slotId) => persistSlot(slotId, next[slotId]))
  }

  const handleChooseProdDay = (slotId, recipeId, date) => {
    const next = (recipesBySlot[slotId] || []).map((p) => (p.recipe.recipeId === recipeId ? { ...p, prodDate: date } : p))
    setRecipesBySlot((prev) => ({ ...prev, [slotId]: next }))
    persistSlot(slotId, next)
  }

  const handleSortSlot = (slotId) => {
    const list = recipesBySlot[slotId] || []
    const rankOf = (p) => catRank.get(p.recipe.cat) ?? Number.MAX_SAFE_INTEGER
    const sorted = [...list].sort((a, b) => rankOf(a) - rankOf(b))
    setRecipesBySlot((prev) => ({ ...prev, [slotId]: sorted }))
    persistSlot(slotId, sorted)
  }

  const handleCardDragStart = (info) => {
    dragInfoRef.current = info
    setDraggedRecipeId(info.recipe.recipeId)
  }

  const handleCardDragEnd = () => {
    dragInfoRef.current = null
    setDraggedRecipeId(null)
    setDropTarget(null)
  }

  const handleZoneDragOver = (slotId, insertBeforeRecipeId) => {
    setDropTarget((prev) => (
      prev && prev.slotId === slotId && prev.insertBeforeRecipeId === insertBeforeRecipeId
        ? prev
        : { slotId, insertBeforeRecipeId }
    ))
  }

  const handleZoneDragLeave = (slotId) => {
    setDropTarget((prev) => (prev && prev.slotId === slotId ? null : prev))
  }

  const handleZoneDrop = (slotId) => {
    const info = dragInfoRef.current
    const insertBeforeRecipeId = dropTarget && dropTarget.slotId === slotId ? dropTarget.insertBeforeRecipeId : null
    dragInfoRef.current = null
    setDraggedRecipeId(null)
    setDropTarget(null)
    if (!info) return

    if (info.sourceSlotId === null) {
      const prodDate = info.displayProdDates.length === 1 ? info.displayProdDates[0] : null
      addPlacementToSlot(slotId, { recipe: info.recipe, prodDate }, insertBeforeRecipeId)
      return
    }

    const fromList = recipesBySlot[info.sourceSlotId] || []
    const placement = fromList.find((p) => p.recipe.recipeId === info.recipe.recipeId)
    if (!placement) return

    const isSameColumn = info.sourceSlotId === slotId
    const baseToList = isSameColumn ? fromList.filter((p) => p.recipe.recipeId !== info.recipe.recipeId) : (recipesBySlot[slotId] || [])

    if (!isSameColumn && baseToList.some((p) => p.recipe.recipeId === placement.recipe.recipeId)) {
      dispatch(openAppMessageSnackBar(DUPLICATE_MESSAGE, "error"))
      return
    }

    const insertIndex = insertBeforeRecipeId ? baseToList.findIndex((p) => p.recipe.recipeId === insertBeforeRecipeId) : baseToList.length
    const newToList = [...baseToList]
    newToList.splice(insertIndex === -1 ? baseToList.length : insertIndex, 0, placement)

    if (isSameColumn) {
      const unchanged = newToList.length === fromList.length && newToList.every((p, i) => p.recipe.recipeId === fromList[i].recipe.recipeId)
      if (unchanged) return
      setRecipesBySlot({ ...recipesBySlot, [slotId]: newToList })
      persistSlot(slotId, newToList)
      return
    }

    const newFromList = fromList.filter((p) => p.recipe.recipeId !== info.recipe.recipeId)
    setRecipesBySlot({
      ...recipesBySlot,
      [info.sourceSlotId]: newFromList,
      [slotId]: newToList,
    })
    persistSlot(info.sourceSlotId, newFromList)
    persistSlot(slotId, newToList)
  }

  return (
    <Box className="height100 overflowHidden" sx={{ display: "flex", flexDirection: "column" }}>
      <GenericTopBarHeader
        onBack={handleBack}
        sx={{ zIndex: 1 }}
        title={
          <PageHeaderTitle
            title="Composition des commandes"
            icon={<RestaurantMenuIcon sx={{ color: COLORS.PRIMARY_COLOR }} />}
            color={COLORS.PRIMARY_COLOR}
            subtitle={headerSubtitle}
            separator="/"
          />
        }
      />

      <Box className="flexColumn stretch overflowHidden flex1" sx={sx.root}>
        {pageLoading ? (
          <Box className="flexCenter flex1" sx={sx.pageLoader}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box className="stretch overflowHidden flex1" sx={sx.main}>
              <Box className="flexColumn stretch overflowHidden flex1" sx={sx.calendarColumn}>
                <RecipeStepper activeStep={0} />
                <RecipeCalendar
                  days={deliveryDays}
                  recipesBySlot={recipesBySlot}
                  onRemoveRecipe={handleRemove}
                  onChooseProdDay={handleChooseProdDay}
                  draggedRecipeId={draggedRecipeId}
                  dropTarget={dropTarget}
                  onCardDragStart={handleCardDragStart}
                  onCardDragEnd={handleCardDragEnd}
                  onZoneDragOver={handleZoneDragOver}
                  onZoneDragLeave={handleZoneDragLeave}
                  onZoneDrop={handleZoneDrop}
                  catRank={catRank}
                  onSortSlot={handleSortSlot}
                />
              </Box>

              <Box className="flexColumn stretch overflowHidden" sx={{ ...sx.panelColumn, width: panelOpen ? PANEL_WIDTH : PANEL_COLLAPSED_WIDTH }}>
                {panelOpen ? (
                  <RecipeCatalogPanel
                    recipes={recipes}
                    categoryOrder={categoryOrder}
                    deliveryDays={deliveryDays}
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    activeCat={activeCat}
                    onCatChange={setActiveCat}
                    selectedProdDays={selectedProdDays}
                    onProdDaysChange={setSelectedProdDays}
                    selectedCategoryTagIds={selectedCategoryTagIds}
                    onCategoryTagIdsChange={setSelectedCategoryTagIds}
                    selectedDishTypeTagIds={selectedDishTypeTagIds}
                    onDishTypeTagIdsChange={setSelectedDishTypeTagIds}
                    recipesBySlot={recipesBySlot}
                    onClose={() => setPanelOpen(false)}
                    onRemoveRecipeEverywhere={handleRemoveEverywhere}
                    onCardDragStart={handleCardDragStart}
                    onCardDragEnd={handleCardDragEnd}
                  />
                ) : (
                  <Box className="flexColumn center" sx={sx.collapsedStrip}>
                    <IconButton size="small" onClick={() => setPanelOpen(true)} aria-label="Ouvrir le catalogue">
                      <MenuIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>

            <Box className="flexRow center justifyEnd" sx={sx.footer}>
              <Button variant="contained" onClick={handleBack} sx={sx.footerBtn}>RENSEIGNER LES VOLUMES</Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default DeliveryMenuRecipe

const sx = {
  root: { position: "relative" },
  pageLoader: { width: "100%" },
  main: { display: "flex", flexDirection: "row", minHeight: 0 },
  calendarColumn: { minWidth: 0, minHeight: 0 },
  panelColumn: {
    flexShrink: 0, borderLeft: "1px solid", borderColor: COLORS.DEFAULT_GREY,
    transition: "width .2s ease",
  },
  collapsedStrip: { height: "100%", pt: 1 },
  footer: { height: "64px", flexShrink: 0, borderTop: "1px solid", borderColor: COLORS.DEFAULT_GREY, px: 3, bgcolor: COLORS.WHITE },
  footerBtn: { height: "40px", px: "22px" },
}
